import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AuthStateResponse, OkResponse, PasswordPolicyView } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { setAuthState } from '../../lib/authState';
import { t } from '../../lib/i18n';
import { ErrorLine, fieldErrors } from '../../components/ErrorPanel';
import { Button, TextField } from '../../components/ui';
import { AuthLayout, useProceed } from './AuthLayout';

/** Login stage `password_change` (core-phase4 s2): the tenant's password has expired, so nothing else works until it is changed. The route wraps it in RequireStage. */
export function PasswordChangePage() {
  const proceed = useProceed();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const policy = useQuery({
    queryKey: ['password-policy'],
    queryFn: () => api.get('/auth/password-policy', { schema: PasswordPolicyView }),
  });
  const change = useMutation({
    mutationFn: () => api.post('/auth/password-change', { body: { currentPassword: current, newPassword: next }, schema: AuthStateResponse }),
    onSuccess: proceed,
  });
  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout', { schema: OkResponse }),
    onSettled: () => setAuthState(null),
  });
  const errs = fieldErrors(change.error);
  const minLength = policy.data?.minLength ?? 8;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    change.mutate();
  };
  return (
    <AuthLayout title={t('auth.passwordChange.title')}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-700">{t('auth.passwordChange.intro')}</p>
        <ErrorLine error={change.error} />
        <TextField
          label={t('auth.currentPassword')}
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          error={errs['currentPassword']}
        />
        <TextField
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          required
          minLength={minLength}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          error={errs['newPassword']}
          hint={
            <>
              {t('auth.passwordRule', { min: minLength })}
              {policy.data && policy.data.reuseCount > 0 ? ` ${t('auth.passwordChange.reuse', { count: policy.data.reuseCount })}` : ''}
            </>
          }
        />
        <Button type="submit" variant="primary" className="w-full" disabled={change.isPending}>
          {t('auth.passwordChange.submit')}
        </Button>
        <p className="text-center text-sm">
          <button type="button" className="text-brand underline" onClick={() => signOut.mutate()}>
            {t('auth.passwordChange.signOut')}
          </button>
        </p>
      </form>
    </AuthLayout>
  );
}
